import { useCallback, useEffect, useRef, useState } from "react";

const useBillingUpgradeOptionsCarouselScroll = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const updateBounds = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    // 1px tolerance: scrollLeft can be fractional and clientWidth/scrollWidth are rounded, so an exact
    // 0 / scrollWidth comparison would intermittently fail to detect the start/end edge.
    setAtStart(track.scrollLeft <= 1);
    setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 1);
  }, []);

  // Recompute on track/viewport resize so the arrows and edge fades only show once the cards actually
  // overflow — when they fit, the cards grow to fill the row and there is nothing to scroll.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    updateBounds();

    const observer = new ResizeObserver(updateBounds);
    observer.observe(track);

    return () => observer.disconnect();
  }, [updateBounds]);

  const scroll = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    // Page by ~90% of the viewport per arrow click, leaving a sliver of overlap so the user keeps
    // visual context of where they were.
    track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: "smooth" });
  }, []);

  return { trackRef, atStart, atEnd, scroll, updateBounds };
};

export default useBillingUpgradeOptionsCarouselScroll;
